/* eslint-disable react-refresh/only-export-components --
 * MasterDataContext と同じ理由。Provider と consumer フックを1ファイルに閉じる。
 */
/**
 * PreferencesContext — 個人設定（PRODUCT_DEV-699）
 * ================================================
 *
 * 設定の**正はサーバ**（`UnizoPreferences`）。localStorage は初期描画用の下書きで、
 * 端末が変わっても・localStorage が消えても引き継がれるようにする。
 * 端末キャッシュと旧キーの吸い上げは `@/lib/preferences/storage` を参照。
 *
 * ## 取得は起動時の1回だけ
 *
 * 従来 `useUiMode` は**使う画面ごとに** `GET /user-preferences` を投げていた。
 * 設定が9キーに増える以上、同じ GET が画面のたびに飛ぶ形は残せない。
 *
 * ## サーバ応答でユーザーの操作を巻き戻さない
 *
 * 応答は数百ミリ秒かかる。その間に操作されたキーは `touched` に入れ、
 * 遅れて届いたサーバ値で上書きしない（`NewDealSheet` の touchedRef と同じ考え方）。
 * これが無いと「選んだ直後に前の値へ戻る」という直しにくい壊れ方をする。
 *
 * ## 書き込みはまとめて送る
 *
 * 音量スライダーや検索履歴は連続で変わる。1操作1リクエストにすると PATCH が溢れるので、
 * `PATCH_DEBOUNCE_MS` ぶん貯めてから1回で送る。画面を閉じる直前は取りこぼすため
 * `pagehide` とアンマウントで必ず吐き出す。
 *
 * ## tenantId は必ず自分で載せる
 *
 * この Context は共通フック（`useApiGet` / `useApiPatch`）ではなく生の `apiGet` / `apiPatch`
 * を使う。React の状態管理の外――`pagehide` のリスナからも投げるため。共通フックが
 * やっていた **tenantId の注入も自前になる**。載せ忘れると `authMiddleware` が DB を引く前に
 * 400 を返し、しかも下書きの localStorage が正しい値を返すので**画面上は正常に見える**。
 * 実際にそれで全リクエストが失敗したまま気づけなかった（dev 169件・prod 75件が全滅）。
 * `PreferencesContext.test.tsx` が tenantId の同梱を検査している。
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { apiGet, apiPatch } from "@shared-ui/utils/api";
import { API_USER_PREFERENCES } from "@/utils/const";
import { useRootLoader } from "@/hooks/useRootLoader";
import type {
  UserPreferences,
  UserPreferencesResponse,
} from "@shared-domain/unizo/contract";
import {
  PREFERENCE_KEYS,
  clearLegacyPreferences,
  readInitialValues,
  writeCache,
  type PreferenceKey,
  type PreferenceValues,
} from "@/lib/preferences/storage";

/** 連続更新をまとめる待ち時間。長くすると取りこぼし、短くすると PATCH が増える。 */
const PATCH_DEBOUNCE_MS = 600;

/**
 * GET の URL。**tenantId は必ず載せる。**
 *
 * `authMiddleware` は DB を引く前に「クエリ → body → パスパラメータ」の順で tenantId を探し、
 * 無ければ 400 を返す（`shared-domain/lib/dynamodb/userTenant.ts`）。共通フック
 * `useApiGet` / `useApiPatch` はこれを自動で注入するが、この Context は生の
 * `apiGet` / `apiPatch` を使う（React の状態管理の外――`pagehide` からも投げるため）。
 * **注入は自前でやる。** `MasterDataContext` が同じ理由で同じことをしている。
 */
const preferencesUrl = (tenantId: string): string =>
  `${API_USER_PREFERENCES}?${new URLSearchParams({ tenantId }).toString()}`;

/**
 * 失敗しても画面は止めない。ただし**痕跡は残す**。
 *
 * 握り潰しきると「1件も保存できていないのに画面は正常」という状態が成立してしまう
 * （下書きの localStorage が正しい値を返すため）。実際にそれで長く潜伏した。
 */
const warnPreferenceFailure =
  (op: string) =>
  (err: unknown): void => {
    console.warn(`preferences: ${op} failed`, err);
  };

export interface PreferencesState {
  /** 解決済みの設定値。未設定のキーは入らない。 */
  values: PreferenceValues;
  /** サーバからの取得が決着したか（失敗も決着に含む）。 */
  isResolved: boolean;
  /** 保存する。null で解除（＝コード既定に戻す）。 */
  setPreference: <K extends PreferenceKey>(
    key: K,
    value: UserPreferences[K],
  ) => void;
}

const PreferencesContext = createContext<PreferencesState | null>(null);

const EMPTY_VALUES: PreferenceValues = {};

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { auth } = useRootLoader();
  // MasterDataContext と同じ導出（403/404 ページでは空文字で、取得をスキップする）
  const tenantId = auth.currentTenantId || auth.memberships[0]?.tenantId || "";

  // 初期値は同期的に読む。記録ドックが useState の初期化関数で値を要求するため、
  // ここが非同期になると開くたびに既定が一瞬出る。
  const [values, setValues] = useState<PreferenceValues>(readInitialValues);
  const [isResolved, setIsResolved] = useState(!tenantId);

  /** ユーザーが操作したキー。サーバ応答で上書きしない。 */
  const touchedRef = useRef<Set<PreferenceKey>>(new Set());
  /** まだ送っていない変更。 */
  const pendingRef = useRef<Partial<UserPreferences>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * `flush` から読む tenantId。
   *
   * `flush` は `pagehide` / `visibilitychange` のリスナに登録するので**同一性を保ちたい**
   * （依存に tenantId を入れると `setPreference` ごと作り直され、リスナも張り直しになる）。
   * ref に逃がして、依存配列は空のまま最新のテナントを読む。
   *
   * 同期を effect でやるのは lint（`react-hooks/refs`）に従うため。`flush` が走るのは
   * デバウンスのタイマー・`pagehide`・アンマウントのいずれかで、どれも effect のあと。
   */
  const tenantIdRef = useRef(tenantId);
  useEffect(() => {
    tenantIdRef.current = tenantId;
  }, [tenantId]);

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const body = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(body).length === 0) return;
    // 保存に失敗しても画面は戻さない。「切り替えたのに戻る」より
    // 「次回の入口が前のまま」のほうが害が小さい（useUiMode 以来の判断）。
    void apiPatch(API_USER_PREFERENCES, {
      ...body,
      tenantId: tenantIdRef.current,
    }).catch(warnPreferenceFailure("patch"));
  }, []);

  const setPreference = useCallback(
    <K extends PreferenceKey>(key: K, value: UserPreferences[K]) => {
      touchedRef.current.add(key);
      setValues((prev) => ({ ...prev, [key]: value }));
      writeCache(key, value);
      pendingRef.current[key] = value;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, PATCH_DEBOUNCE_MS);
    },
    [flush],
  );

  // 画面を閉じる / 裏に回るときに貯めたぶんを吐き出す。
  // visibilitychange まで見るのは、モバイルでは pagehide が来ないまま
  // アプリが落とされることがあるため。
  useEffect(() => {
    const onLeave = () => flush();
    window.addEventListener("pagehide", onLeave);
    document.addEventListener("visibilitychange", onLeave);
    return () => {
      window.removeEventListener("pagehide", onLeave);
      document.removeEventListener("visibilitychange", onLeave);
      flush();
    };
  }, [flush]);

  useEffect(() => {
    // 認証前 / 403・404 ページ。取りに行かないので決着済みのまま（初期値が `!tenantId`）。
    if (!tenantId) return;

    let aborted = false;

    void apiGet<UserPreferencesResponse>(preferencesUrl(tenantId))
      .then((res) => {
        if (aborted || !res.success) return;
        applyServerValues(res.preferences);
        seedFromLocal(res.preferences);
      })
      // 取得に失敗しても下書きで動く。ここで止めると設定1本で画面が使えなくなる。
      .catch(warnPreferenceFailure("get"))
      .finally(() => {
        if (!aborted) setIsResolved(true);
      });

    /** サーバ値を反映する。操作済みのキーと、サーバが未設定のキーは触らない。 */
    function applyServerValues(prefs: UserPreferences): void {
      const applied: PreferenceValues = {};
      for (const key of PREFERENCE_KEYS) {
        if (touchedRef.current.has(key)) continue;
        const v = prefs[key];
        // null は「サーバは持っていない」。下書きを残す（次の seed で拾う）。
        if (v === null || v === undefined) continue;
        (applied as Record<string, unknown>)[key] = v;
        writeCache(key, v);
      }
      if (Object.keys(applied).length === 0) return;
      setValues((prev) => ({ ...prev, ...applied }));
    }

    /**
     * 端末に残っている値を、サーバが持っていないぶんだけ1回で上げる。
     *
     * **9キーを1回の PATCH にまとめる。** 分けると全ユーザーの初回アクセスで
     * 9リクエストが一斉に走る。
     *
     * ⚠ **取り込み元は旧キーではなく `readInitialValues()`（＝キャッシュ優先で重ねた現在値）。**
     * 旧キーだけを見ると、「サーバへの保存が効いていない期間にユーザーが選んだ値」を
     * 取りこぼす。その期間の選択はキャッシュ（`unizo-pref:*`）にしか無く、旧キーには
     * それより**古い**値が残っているためで、旧キーを上げると設定がその古い値へ静かに
     * 巻き戻る（`writeCache` でキャッシュまで上書きされるので、次の起動から表に出る）。
     */
    function seedFromLocal(prefs: UserPreferences): void {
      const local = readInitialValues();
      const body: Record<string, unknown> = {};
      for (const [field, value] of Object.entries(local)) {
        const key = field as PreferenceKey;
        // 操作済みのキーは通常の保存が既に運んでいる。ここで重ねると PATCH が2本になる。
        if (touchedRef.current.has(key)) continue;
        if (prefs[key] == null) body[field] = value;
      }
      if (Object.keys(body).length === 0) {
        clearLegacyPreferences();
        return;
      }
      void apiPatch(API_USER_PREFERENCES, { ...body, tenantId })
        .then(() => {
          // 消すのは成功したときだけ。失敗したら次回のアクセスでやり直す。
          clearLegacyPreferences();
          for (const [field, value] of Object.entries(body)) {
            writeCache(field as PreferenceKey, value);
          }
        })
        .catch(warnPreferenceFailure("seed"));
    }

    return () => {
      aborted = true;
    };
  }, [tenantId]);

  const value = useMemo<PreferencesState>(
    () => ({ values, isResolved, setPreference }),
    [values, isResolved, setPreference],
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

/**
 * Provider の外では「取得済みで全部未設定」を返す。
 *
 * throw すると、設定を1つ読むだけの小さなコンポーネントの単体テストまで
 * Provider を要求することになる（MasterDataContext と同じ判断）。
 */
export function usePreferences(): PreferencesState {
  return (
    useContext(PreferencesContext) ?? {
      values: EMPTY_VALUES,
      isResolved: true,
      setPreference: () => {},
    }
  );
}

/**
 * 設定を1つ読み書きする。未設定なら `fallback`。
 *
 * `useState` と同じ形で返すので、`const [x, setX] = useState(...)` を
 * そのまま置き換えられる。
 */
export function usePreference<K extends PreferenceKey>(
  key: K,
  fallback: NonNullable<UserPreferences[K]>,
): [NonNullable<UserPreferences[K]>, (value: UserPreferences[K]) => void] {
  const { values, setPreference } = usePreferences();
  const value = (values[key] ?? fallback) as NonNullable<UserPreferences[K]>;
  const set = useCallback(
    (next: UserPreferences[K]) => setPreference(key, next),
    [key, setPreference],
  );
  return [value, set];
}

/**
 * 「未設定」を区別したいとき用（既定を呼び出し側が決める設定）。
 * 例: UI モードは未設定なら管理者は管理者ビュー、それ以外は個人ビュー。
 */
export function useOptionalPreference<K extends PreferenceKey>(
  key: K,
): [UserPreferences[K], (value: UserPreferences[K]) => void, boolean] {
  const { values, setPreference, isResolved } = usePreferences();
  const set = useCallback(
    (next: UserPreferences[K]) => setPreference(key, next),
    [key, setPreference],
  );
  return [(values[key] ?? null) as UserPreferences[K], set, isResolved];
}
